import axios from 'axios';
import { VEHICLE_TYPE_MAPPER, TRANSACTION_TYPE_MAPPER, PLATE_TYPE_FULL_TO_SHORT } from '../../components/fees/contants';
import { getDefaultRegistrationMonths } from '../../components/utility/registrationMonths';
import { isRefinance } from '../../components/utility/transactionType';

const SERVER_URL = process.env.REACT_APP_API_URL;

export const Api = {
  getCsrfToken: async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/api/get-csrf-token`, {
        withCredentials: true,
      });
      return response.data.csrf_token;
    } catch (e) {
      console.error('Error fetching CSRF token:', e);
      throw new Error('Failed to get CSRF token.');
    }
  },


  decodeZip: async (data) => {
    const { zipValue } = data
    try {
      const csrfToken = await Api.getCsrfToken();
      const response = await axios.post(`${SERVER_URL}/api/decode-zip`, {
        zip: zipValue,
      },{
        headers: {
          'X-CSRFToken': csrfToken,
        },
        withCredentials: true
      });

      switch (response.status) {
        case 200:
          return response;
        case 401:
          throw new Error('Invalid email or password!');
        case 404:
          throw new Error('User does not exists!');
        case 400:
          throw new Error('All fields required!');
        default:
          throw new Error('Something went wrong!');
      }
    } catch (e) {
      console.log("Error", e);
    }
  },

  decodeVin: async (data) => {
    const { vin } = data
    try {
      const csrfToken = await Api.getCsrfToken();
      const response = await axios.post(`${SERVER_URL}/api/decode-vin`, {
        vin: vin,
      }, {
        headers:{
          'X-CSRFToken': csrfToken,
        },
        withCredentials: true
      });

      switch (response.status) {
        case 200:
          return response;
        case 401:
          throw new Error('Invalid email or password!');
        case 404:
          throw new Error('User does not exists!');
        case 400:
          throw new Error('All fields required!');
        default:
          throw new Error('Something went wrong!');
      }
    } catch (e) {
      console.log("Error", e);
    }
  },

  submitFee: async (data) => {
    try {
      const payload = {
        "": "T",
        ZIP: data.zipCode,
        zip: data.zipCode,
        City: data.city,
        city: data.city,
        County: data.country,
        county: data.country,
        State: data.state,
        state: data.state,
        VIN: data.vinCode,
        vinNumber: data.vinCode,
        VehicleType: VEHICLE_TYPE_MAPPER[data.vehicleType],
        vehicleType: data.vehicleType,
        FuelType: data.fuelType,
        fuelType: data.fuelType,
        modelYear: data.modelYear,
        Weight: data.vehicleWeight || "3000",
        MSRP: data.msrp,
        // Populated by the VIN decode and already held in vehicleInfo, but never
        // forwarded — so the bot sent <Cylinders> from its decode and the web app
        // sent nothing for the same vehicle.
        cylinders: data.cylinders,
        gvw: data.gvw,
        BasePrice: data.basePrice,
        PlateType: PLATE_TYPE_FULL_TO_SHORT[data.plateType],
        TransactionType: TRANSACTION_TYPE_MAPPER[data.transactionType],
        TitleStatus: data.titleStatus,
        OutsideCityLimits: data.outSideCityLimit ? "1" : "0",
        IsTransfer: data.plateTransfer ? "1" : "0",
        GVW: data.gvw,
        CustomerType: "F",
        LienStatus: "1",
        DocFees: data.docsFees,
        ExtendedWarrantiesField: data.extendedWarranties,
        Additions: data.Additions,
        // These two were sent as "ProtectionPackages"/"TheftProtections" while
        // fees.py reads "ProtectionPackageField"/"TheftProtectionField" — the
        // documented CULS Fees API names — so both values were silently dropped
        // and never reached ATC. They're taxable, so this moved the quote.
        ProtectionPackageField: data.ProtectionPackageField,
        TheftProtectionField: data.TheftProtectionField,
        InsurancePackages: data.InsurancePackages,
        DeliveryFees: data.DeliveryFees,
        // Trade-in and rebate figures are purchase-only (see
        // components/utility/transactionType.js). refi_fees.py builds its inputs
        // from a fixed allow-list that never references TradeIn*/Rebate, so these
        // are dropped server-side for a refi regardless — but a user who enters
        // them as a Purchase and then switches to Refinance still has them in
        // redux, so blank them here rather than shipping figures that silently
        // do nothing and contradict the hidden inputs.
        TradeInValue: isRefinance(data) ? "" : data.TradeInValue,
        TradeInYear: isRefinance(data) ? "" : data.TradeInYear,
        ManufacturerRebate: isRefinance(data) ? "" : data.ManufacturerRebate,
        DealerServiceFees: data.DealerServiceFees,
        DealerInventoryTax: data.DealerInventoryTax,
        Discounts: data.Discounts,
        // Per ATC AGF Consumption Guide: RegistrationOptions accepts
        // "M" (Military) or "B" (Business). Only send "M" when the
        // Military toggle on the Fees form is enabled.
        RegistrationOptions: data.military ? "M" : "",
        // Broken-out MPG fields per ATC AGF Consumption Guide:
        //   <MPG>         = City miles per gallon (Integer)
        //   <HighwayMPG>  = Highway miles per gallon (Integer)
        //   <CombinedMPG> = Combined miles per gallon (Integer)
        // The legacy `mpg` field (combined) is sent as a fallback for the
        // <MPG> tag so existing transactions keep working when the city
        // value wasn't autofilled.
        MPG: data.cityMpg || data.mpg || "",
        HighwayMPG: data.highwayMpg || "",
        CombinedMPG: data.combinedMpg || data.mpg || "",
        // Lowercase `mpg` for refi_fees, which reads `options.get('mpg', '')`
        // and never looks at the uppercase tags — so on a Refinance the MPG
        // the user typed was dropped entirely and its state-requirement check
        // (Oregon, DC) could only ever be satisfied by the VIN decode.
        // Additive: fees.py resolves CityMPG from 'MPG' first, which is always
        // present in this payload, so the purchase path is unaffected.
        mpg: data.cityMpg || data.combinedMpg || data.mpg || "",
        // Registration term. The Fees form has no input for this today, so this
        // falls back to the per-state default (24 for Oregon, 12 elsewhere — see
        // utility/registrationMonths.js); if an input is added later the submitted
        // value flows through without another change here. fees.py applies the same
        // per-state default when the key is absent, but sending it explicitly is
        // what makes a user-specified term reachable at all.
        registrationmonths: data.registrationMonths || getDefaultRegistrationMonths(data.state),
      }

      const csrfToken = await Api.getCsrfToken();
      const response = await axios.post(`${SERVER_URL}/api/fees-api-callV2`, payload, {
        headers: {
          'X-CSRFToken': csrfToken,
        },
        withCredentials: true,
      });


      return response;
    } catch (e) {
      return {
        error: true,
        message: e?.response?.data?.message || e?.response?.data?.error || 'Something went wrong!',
        status: e?.response?.status || 500,
      };
    }
  },
}